import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ExpandLess,
  ExpandMore,
  LinkOff as LinkOffIcon,
  ContentCut as ContentCutIcon,
  PlayArrow as PlayArrowIcon,
  QrCode2 as QrCode2Icon,
  Send as SendIcon,
  Settings as SettingsIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import axios from '../../utils/axios';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/ui';
import ProductsTabs from '../../components/products/ProductsTabs';
import MsStoresDialog from '../../components/products/MsStoresDialog';
import { MsBulkForcedDialog, MsProductForcedDialog } from '../../components/products/MsForcedDialog';
import { forcedBindingsCount, productBroadcastOn, productHasLinks, storeOptions } from '../../components/products/msAssortmentApi.mjs';

const MP_LABEL = { wb: 'WB', ozon: 'Ozon', yandex: 'Yandex' };
const PAGE_SIZE = 50;
const COL_COUNT = 10;
const REPLENISH_POLL_MS = 1500;

function shopLabel(shop) {
  return shop === 'sewing' ? 'Швейка' : 'Производство';
}

function notifyReplenishResult(notify, data, shop) {
  if (data?.skipped) {
    notify.warning(data.message || `Пропуск: ${data.reason}`);
    return;
  }
  const errCount = Array.isArray(data?.errors) ? data.errors.length : 0;
  const msg = `${shopLabel(shop)}: заданий ${data?.created_tasks?.length ?? data?.tasks ?? 0}, строк ${data?.productions ?? 0}, перемещений ${data?.created_moves?.length ?? data?.moves ?? 0}`;
  if (errCount) notify.warning(`${msg}. Ошибок: ${errCount}`);
  else notify.success(`Пополнение записано в МойСклад. ${msg}`);
}

function msProductLabel(...candidates) {
  for (const value of candidates) {
    const text = String(value == null ? '' : value).trim();
    if (text) return text;
  }
  return '—';
}

function ruBindingCount(n) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} связка`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} связки`;
  return `${n} связок`;
}

function ellipsisText(value) {
  const text = String(value == null ? '' : value).trim();
  return text || '—';
}

function linkSkuLabel(link) {
  if (link?.sku != null && String(link.sku).trim()) return String(link.sku).trim();
  if (String(link?.marketplace || '').toLowerCase() === 'yandex') return '';
  return link?.product_key ? String(link.product_key) : '';
}

function linkMatchesQuery(link, query) {
  const needle = String(query || '').trim().toLowerCase();
  if (!needle) return false;
  return [link?.article, link?.sku, link?.product_key, link?.offer_id]
    .some((value) => String(value || '').toLowerCase().includes(needle));
}

function rowMatchesMpQuery(row, query) {
  return (row?.links || []).some((link) => linkMatchesQuery(link, query));
}

function EllipsisCell({ children, title, sx }) {
  const label = ellipsisText(title != null ? title : children);
  return (
    <TableCell sx={{ overflow: 'hidden', ...sx }}>
      <Tooltip title={label === '—' ? '' : label}>
        <Typography variant="body2" noWrap component="span" sx={{ display: 'block' }}>
          {children == null || children === '' ? '—' : children}
        </Typography>
      </Tooltip>
    </TableCell>
  );
}

export default function MsProducts() {
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [unlinkedMpArticles, setUnlinkedMpArticles] = useState([]);
  const [linkFilter, setLinkFilter] = useState('all');
  const [withMinMax, setWithMinMax] = useState(false);
  const [withoutPlan, setWithoutPlan] = useState(false);
  const [includeStock, setIncludeStock] = useState(false);
  const [disabledLinksOnly, setDisabledLinksOnly] = useState(false);
  const [withForced, setWithForced] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [forcedRow, setForcedRow] = useState(null);
  const [bulkForcedOpen, setBulkForcedOpen] = useState(false);
  const [massBusy, setMassBusy] = useState(false);
  const [sendingStocks, setSendingStocks] = useState(false);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [stores, setStores] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);

  const [linkFor, setLinkFor] = useState(null);
  const [linkMp, setLinkMp] = useState('wb');
  const [accounts, setAccounts] = useState([]);
  const [linkAccount, setLinkAccount] = useState('');
  const [mpSearch, setMpSearch] = useState('');
  const [mpOptions, setMpOptions] = useState([]);
  const [mpPicked, setMpPicked] = useState(null);
  const [replenishShop, setReplenishShop] = useState(null);
  const [replenishing, setReplenishing] = useState(false);
  const [replenishProgress, setReplenishProgress] = useState(null);
  const replenishPollRef = useRef(null);
  const [runs, setRuns] = useState([]);
  const [matching, setMatching] = useState(false);
  const [matchReport, setMatchReport] = useState(null);
  const [matchTab, setMatchTab] = useState('created');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/ms-assortment/products', {
        params: {
          q: q || undefined,
          limit: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE,
          mapped_only: linkFilter === 'mapped' ? 1 : undefined,
          unmapped_only: linkFilter === 'unmapped' ? 1 : undefined,
          with_minmax: withMinMax ? 1 : undefined,
          without_plan: withoutPlan ? 1 : undefined,
          include_stock: includeStock ? 1 : undefined,
          disabled_links_only: disabledLinksOnly ? 1 : undefined,
          with_forced: withForced ? 1 : undefined,
        },
      });
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setCount(Number(data?.count) || 0);
      setUnlinkedMpArticles(Array.isArray(data?.unlinked_mp_articles) ? data.unlinked_mp_articles : []);
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Не удалось загрузить товары МС');
    } finally {
      setLoading(false);
    }
  }, [q, page, linkFilter, withMinMax, withoutPlan, includeStock, disabledLinksOnly, withForced, notify]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setSelectedIds(new Set());
    setExpanded({});
  }, [q, page, linkFilter, withMinMax, withoutPlan, disabledLinksOnly, withForced]);

  const loadSettings = useCallback(async () => {
    try {
      const [{ data: st }, storesRes, runsRes] = await Promise.all([
        axios.get('/ms-assortment/settings'),
        axios.get('/mswb/ms-stores', { silent: true }).catch(() => ({ data: [] })),
        axios.get('/ms-assortment/runs', { params: { limit: 5 }, silent: true }).catch(() => ({ data: { rows: [] } })),
      ]);
      setSettings(st);
      setStores(storeOptions(storesRes.data));
      setRuns(Array.isArray(runsRes.data?.rows) ? runsRes.data.rows : []);
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Не удалось загрузить настройки');
    }
  }, [notify]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const storeById = useMemo(() => new Map(stores.map((s) => [s.id, s])), [stores]);

  const saveRow = async (row, patch) => {
    setSavingId(row.ms_id);
    try {
      await axios.put(`/ms-assortment/products/${row.ms_id}`, patch);
      await load();
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Не удалось сохранить');
    } finally {
      setSavingId(null);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await axios.put('/ms-assortment/settings', settings);
      setSettings(data);
      notify.success('Настройки складов сохранены');
      setSettingsOpen(false);
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Не удалось сохранить настройки');
    } finally {
      setSavingSettings(false);
    }
  };

  const openLinkDialog = async (row) => {
    setLinkFor(row);
    setLinkMp('wb');
    setLinkAccount('');
    setMpSearch('');
    setMpPicked(null);
    setMpOptions([]);
    try {
      const { data } = await axios.get('/mswb/wb-accounts');
      setAccounts(Array.isArray(data) ? data : []);
    } catch (_) {
      setAccounts([]);
    }
  };

  const changeLinkMp = async (mp) => {
    setLinkMp(mp);
    setLinkAccount('');
    setMpPicked(null);
    setMpOptions([]);
    const url = mp === 'wb' ? '/mswb/wb-accounts' : (mp === 'ozon' ? '/mswb/ozon-accounts' : '/mswb/yandex-accounts');
    try {
      const { data } = await axios.get(url);
      setAccounts(Array.isArray(data) ? data : []);
    } catch (_) {
      setAccounts([]);
    }
  };

  const searchMpProducts = async () => {
    if (!linkAccount) return;
    const url = linkMp === 'wb' ? '/mswb/wb-products' : (linkMp === 'ozon' ? '/mswb/ozon-products' : '/mswb/yandex-products');
    try {
      const { data } = await axios.get(url, {
        params: { q: mpSearch, account_id: linkAccount, only_unmapped: 'true', limit: 30, offset: 0 },
      });
      setMpOptions(Array.isArray(data?.rows) ? data.rows : []);
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Не удалось найти товары МП');
    }
  };

  const addLink = async () => {
    if (!linkFor || !mpPicked || !linkAccount) return;
    const productKey = linkMp === 'wb'
      ? mpPicked.nm_id
      : (linkMp === 'ozon'
        ? (mpPicked.product_id || mpPicked.id)
        : (mpPicked.product_id || mpPicked.offer_id || mpPicked.shop_sku));
    try {
      await axios.post(`/ms-assortment/products/${linkFor.ms_id}/links`, {
        marketplace: linkMp,
        account_id: Number(linkAccount),
        product_key: String(productKey),
      });
      notify.success('Связь добавлена');
      setLinkFor(null);
      await load();
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Не удалось добавить связь');
    }
  };

  const removeLink = async (row, link) => {
    try {
      await axios.delete(`/ms-assortment/products/${row.ms_id}/links`, {
        data: {
          marketplace: link.marketplace,
          account_id: link.account_id,
          product_key: link.product_key,
          ms_id: row.ms_id,
        },
      });
      await load();
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Не удалось отвязать');
    }
  };

  const patchLinkEnabled = async (row, link, enabled) => {
    await axios.patch(`/ms-assortment/products/${row.ms_id}/links`, {
      marketplace: link.marketplace,
      account_id: link.account_id,
      product_key: link.product_key,
      enabled,
    });
  };

  const toggleLinkEnabled = async (row, link, enabled) => {
    setRows((list) => list.map((item) => (
      item.ms_id !== row.ms_id
        ? item
        : {
          ...item,
          links: (item.links || []).map((entry) => (
            entry.marketplace === link.marketplace
              && Number(entry.account_id) === Number(link.account_id)
              && String(entry.product_key) === String(link.product_key)
              ? { ...entry, enabled }
              : entry
          )),
        }
    )));
    try {
      await patchLinkEnabled(row, link, enabled);
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Не удалось изменить связь');
      await load();
    }
  };

  const pageIds = useMemo(() => rows.map((row) => row.ms_id), [rows]);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const somePageSelected = pageIds.some((id) => selectedIds.has(id));

  const toggleSelect = (msId, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(msId);
      else next.delete(msId);
      return next;
    });
  };

  const toggleSelectPage = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of pageIds) {
        if (checked) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  };

  const applyMsBroadcast = (list, msIds, enabled) => {
    const ids = new Set((msIds || []).map(String));
    return list.map((item) => (
      ids.has(String(item.ms_id))
        ? { ...item, links: (item.links || []).map((entry) => ({ ...entry, enabled })) }
        : item
    ));
  };

  const setProductBroadcast = async (row, enabled) => {
    if (!productHasLinks(row)) {
      notify.warning('У товара нет связей');
      return;
    }
    setRows((list) => applyMsBroadcast(list, [row.ms_id], enabled));
    try {
      const { data } = await axios.patch('/ms-assortment/links', { ms_ids: [row.ms_id], enabled });
      const updated = Array.isArray(data?.items) ? data.items.length : 0;
      notify.success(enabled
        ? `Трансляция включена (${updated} связей)`
        : `Трансляция выключена (${updated} связей)`);
      await load();
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Не удалось изменить трансляцию');
      await load();
    }
  };

  const massSetLinksEnabled = async (enabled) => {
    const msIds = [...selectedIds];
    if (!msIds.length) {
      notify.warning('Выделите товары');
      return;
    }
    setMassBusy(true);
    try {
      const { data } = await axios.patch('/ms-assortment/links', { ms_ids: msIds, enabled });
      const items = Array.isArray(data?.items) ? data.items : [];
      const appliedMs = new Set(items.map((item) => String(item.ms_id)));
      const applied = msIds.filter((id) => appliedMs.has(String(id))).length;
      const skipped = msIds.length - applied;
      if (skipped && applied) {
        notify.warning(`${enabled ? 'Включена' : 'Выключена'} трансляция: ${applied}, без связей: ${skipped}`);
      } else if (skipped && !applied) {
        notify.warning('У выделенных товаров нет связей');
      } else {
        notify.success(enabled
          ? `Включена трансляция: ${applied} товаров`
          : `Выключена трансляция: ${applied} товаров`);
      }
      await load();
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Не удалось изменить трансляцию');
    } finally {
      setMassBusy(false);
    }
  };

  const sendSelectedStocks = async () => {
    const msIds = [...selectedIds];
    if (!msIds.length) {
      notify.warning('Выделите товары');
      return;
    }
    setSendingStocks(true);
    try {
      const { data } = await axios.post('/ms-assortment/products/stocks/sync', { ms_ids: msIds });
      notify.success(`Остатки отправлены: ${data?.items ?? data?.skusSent ?? msIds.length} позиций`);
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Не удалось отправить остатки');
    } finally {
      setSendingStocks(false);
    }
  };

  const storesReady = Boolean(
    settings?.production_store_id
    && settings?.materials_store_id
    && (settings?.gp_store_ids || []).length,
  );
  const sewingStoresReady = Boolean(
    settings?.sewing?.production_store_id
    && settings?.sewing?.materials_store_id
    && (settings?.sewing?.gp_store_ids || []).length,
  );

  const runBarcodeMatch = async () => {
    setMatching(true);
    try {
      const { data } = await axios.post('/ms-assortment/auto-link-by-barcode');
      const createdN = (data?.created || []).length;
      const reboundN = (data?.rebound || []).length;
      const conflictN = (data?.conflicts || []).length;
      setMatchTab(createdN ? 'created' : reboundN ? 'rebound' : conflictN ? 'conflicts' : 'created');
      setMatchReport(data);
      await load();
      const created = data?.stats?.created ?? 0;
      const rebound = data?.stats?.rebound ?? 0;
      const conflicts = data?.stats?.conflicts ?? 0;
      const already = data?.stats?.already ?? 0;
      const summary = `Сопоставлено ${created}. Перенесено с архива ${rebound}. Уже было ${already}`;
      if (conflicts) {
        notify.warning(`${summary}. Конфликтов: ${conflicts}`);
      } else {
        notify.success(summary);
      }
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Не удалось сопоставить по штрихкоду');
    } finally {
      setMatching(false);
    }
  };

  const stopReplenishPoll = useCallback(() => {
    if (replenishPollRef.current) {
      clearTimeout(replenishPollRef.current);
      replenishPollRef.current = null;
    }
  }, []);

  useEffect(() => () => stopReplenishPoll(), [stopReplenishPoll]);

  const waitReplenishJob = useCallback((job) => new Promise((resolve, reject) => {
    const id = String(job?.job_id || '').trim();
    if (!id) {
      reject(new Error('Не удалось запустить пополнение'));
      return;
    }
    setReplenishProgress(job);
    const tick = async () => {
      try {
        const { data } = await axios.get(`/ms-assortment/jobs/${encodeURIComponent(id)}`, { silent: true });
        setReplenishProgress(data);
        if (data?.status === 'completed' || data?.status === 'failed') {
          resolve(data);
          return;
        }
        replenishPollRef.current = setTimeout(tick, REPLENISH_POLL_MS);
      } catch (error) {
        if (error?.response?.status === 404) {
          reject(new Error('Задание прервалось после перезапуска сервера. Запустите снова.'));
          return;
        }
        replenishPollRef.current = setTimeout(tick, REPLENISH_POLL_MS);
      }
    };
    tick();
  }), []);

  const runReplenish = async () => {
    const shop = replenishShop;
    if (!shop) return;
    setReplenishing(true);
    stopReplenishPoll();
    try {
      let job;
      try {
        const { data } = await axios.post('/ms-assortment/replenish', { dry_run: false, shop }, { silent: true });
        job = data;
      } catch (error) {
        const body = error?.response?.data;
        if (error?.response?.status === 409 && body?.details?.job_id) {
          if (body.details.shop && body.details.shop !== shop) {
            notify.warning('Автопополнение уже выполняется');
            return;
          }
          job = body.details;
        } else {
          throw error;
        }
      }
      const done = await waitReplenishJob(job);
      if (done.status === 'failed') {
        notify.error(done.error_message || 'Не удалось запустить пополнение');
        return;
      }
      const data = done.result || {};
      setReplenishShop(null);
      setReplenishProgress(null);
      await loadSettings();
      notifyReplenishResult(notify, data, shop);
    } catch (error) {
      notify.error(error?.response?.data?.message || error.message || 'Не удалось запустить пополнение');
    } finally {
      stopReplenishPoll();
      setReplenishing(false);
    }
  };

  const pages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <>
      <ProductsTabs />
      <PageHeader
        title="Товары МойСклад"
        subtitle="Связи МП, техкарты, min/max и автопополнение остатков"
        breadcrumbs={[
          { label: 'Товары', path: '/analytics/products' },
          { label: 'Товары МС' },
        ]}
        actions={(
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              startIcon={<QrCode2Icon />}
              disabled={matching}
              onClick={runBarcodeMatch}
            >
              {matching ? 'Сопоставляем…' : 'Сопоставить по ШК'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<SendIcon />}
              disabled={!selectedIds.size || sendingStocks}
              onClick={sendSelectedStocks}
            >
              {sendingStocks ? 'Отправляем…' : 'Отправить остатки'}
            </Button>
            <Button variant="outlined" startIcon={<SettingsIcon />} onClick={() => { loadSettings(); setSettingsOpen(true); }}>
              Склады
            </Button>
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              disabled={!storesReady || replenishing}
              onClick={() => setReplenishShop('production')}
            >
              Производство
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<ContentCutIcon />}
              disabled={!sewingStoresReady || replenishing}
              onClick={() => setReplenishShop('sewing')}
            >
              Швейка
            </Button>
          </Stack>
        )}
      />

      {settings && !settings.production_store_in_gp && settings.production_store_id && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Склад производства не входит в склады ГП. Ожидание задания не попадёт в «доступно», и cron может создать лишние задания.
        </Alert>
      )}
      {settings && !settings.sewing_store_in_gp && settings.sewing?.production_store_id && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Склад швейки не входит в склады ГП швейки. Ожидание задания не попадёт в «доступно», и cron может создать лишние задания.
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <TextField
            size="small"
            label="Поиск"
            value={q}
            onChange={(e) => { setPage(1); setQ(e.target.value); }}
            placeholder="Название, ШК, id МС или offer_id МП"
            helperText="Можно искать по товару МС и по артикулу МП (offer_id)"
            sx={{ minWidth: 280 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Связи МП</InputLabel>
            <Select
              label="Связи МП"
              value={linkFilter}
              onChange={(e) => { setPage(1); setLinkFilter(e.target.value); }}
            >
              <MenuItem value="all">Все товары</MenuItem>
              <MenuItem value="mapped">Только со связями</MenuItem>
              <MenuItem value="unmapped">Только без связей</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch size="small" checked={withMinMax} onChange={(e) => { setPage(1); setWithMinMax(e.target.checked); }} />}
            label="Только с min/max"
          />
          <FormControlLabel
            control={<Switch size="small" checked={withoutPlan} onChange={(e) => { setPage(1); setWithoutPlan(e.target.checked); }} />}
            label="Без техкарты"
          />
          <FormControlLabel
            control={<Switch size="small" checked={includeStock} onChange={(e) => setIncludeStock(e.target.checked)} />}
            label="Показать доступно"
          />
          <FormControlLabel
            control={<Switch size="small" checked={disabledLinksOnly} onChange={(e) => { setPage(1); setDisabledLinksOnly(e.target.checked); }} />}
            label="Только выключенные связи"
          />
          <FormControlLabel
            control={<Switch size="small" checked={withForced} onChange={(e) => { setPage(1); setWithForced(e.target.checked); }} />}
            label="Есть принуд"
          />
          <Button startIcon={<SyncIcon />} onClick={load} disabled={loading}>Обновить</Button>
        </Stack>
        <Typography variant="bodySmall" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Min/max и техкарта — на товаре МС. Вкл пишет только связь, принуд — на товар и складскую связку.
          Трансляция — только передача остатков на МП; заказы в МС, подбор и перемещения идут по связи независимо от неё.
        </Typography>
        {selectedIds.size > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
            <Typography variant="body2" sx={{ alignSelf: 'center' }}>Выбрано: {selectedIds.size}</Typography>
            <Button size="small" disabled={massBusy} onClick={() => massSetLinksEnabled(true)}>Включить трансляцию</Button>
            <Button size="small" disabled={massBusy} onClick={() => massSetLinksEnabled(false)}>Выключить трансляцию</Button>
            <Button size="small" disabled={massBusy} onClick={() => setBulkForcedOpen(true)}>Принуд</Button>
          </Stack>
        )}
      </Paper>

      {!loading && q.trim() && rows.length === 0 && unlinkedMpArticles.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Артикул МП найден, но не привязан к товару МС:
          {' '}
          {unlinkedMpArticles.map((item) => `${MP_LABEL[item.marketplace] || item.marketplace}: ${item.offer_id}`).join(', ')}
        </Alert>
      )}

      <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
        <TableContainer
          component={Paper}
          sx={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}
        >
          <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: 42 }}>
                  <Checkbox
                    size="small"
                    checked={allPageSelected}
                    indeterminate={somePageSelected && !allPageSelected}
                    onChange={(e) => toggleSelectPage(e.target.checked)}
                    inputProps={{ 'aria-label': 'Выбрать все на странице' }}
                  />
                </TableCell>
                <TableCell sx={{ width: 48 }} />
                <TableCell sx={{ width: '24%' }}>Товар МС</TableCell>
                <TableCell sx={{ width: '14%' }}>ШК</TableCell>
                <TableCell sx={{ width: 120 }}>МП</TableCell>
                <TableCell sx={{ width: 96 }}>Трансляция</TableCell>
                <TableCell sx={{ width: 88 }}>Min</TableCell>
                <TableCell sx={{ width: 88 }}>Max</TableCell>
                <TableCell sx={{ width: '20%' }}>Техкарта</TableCell>
                {includeStock && <TableCell align="right" sx={{ width: 88 }}>Доступно</TableCell>}
                <TableCell sx={{ width: 120 }}>Принуд</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const autoOpen = rowMatchesMpQuery(row, q);
                const open = expanded[row.ms_id] != null ? !!expanded[row.ms_id] : autoOpen;
                const colSpan = includeStock ? COL_COUNT + 1 : COL_COUNT;
                return (
                  <Fragment key={row.ms_id}>
                    <TableRow hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selectedIds.has(row.ms_id)}
                          onChange={(e) => toggleSelect(row.ms_id, e.target.checked)}
                          inputProps={{ 'aria-label': `Выбрать ${row.name || row.ms_id}` }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => setExpanded((s) => ({ ...s, [row.ms_id]: !open }))}>
                          {open ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ overflow: 'hidden' }}>
                        <Tooltip title={row.name || ''}>
                          <Typography variant="body2" noWrap>{row.name || '—'}</Typography>
                        </Tooltip>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                          {row.ms_id}
                        </Typography>
                      </TableCell>
                      <EllipsisCell title={row.barcode}>{row.barcode || '—'}</EllipsisCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                          {(row.links || []).length
                            ? [...new Set(row.links.map((l) => MP_LABEL[l.marketplace] || l.marketplace))].map((label) => (
                              <Chip key={label} size="small" label={label} />
                            ))
                            : <Chip size="small" variant="outlined" label="нет связей" />}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const hasLinks = productHasLinks(row);
                          const broadcastOn = productBroadcastOn(row);
                          return (
                            <Tooltip title={hasLinks
                              ? (broadcastOn ? 'Выключить трансляцию всех связей товара' : 'Включить трансляцию всех связей товара')
                              : 'Нет связей'}>
                              <span>
                                <Switch
                                  size="small"
                                  checked={broadcastOn}
                                  disabled={!hasLinks}
                                  onChange={(e) => setProductBroadcast(row, e.target.checked)}
                                  inputProps={{ 'aria-label': 'Трансляция товара' }}
                                />
                              </span>
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          fullWidth
                          value={row.min_stock ?? ''}
                          disabled={savingId === row.ms_id}
                          sx={{ minWidth: 0 }}
                          onBlur={(e) => {
                            const v = e.target.value === '' ? null : Number(e.target.value);
                            if (v === (row.min_stock ?? null) && row.max_stock == null) return;
                            if (v == null && row.max_stock == null) {
                              saveRow(row, { min_stock: null, max_stock: null });
                              return;
                            }
                            if (v == null || row.max_stock == null) return;
                            saveRow(row, { min_stock: v, max_stock: row.max_stock });
                          }}
                          onChange={(e) => {
                            const v = e.target.value === '' ? null : Number(e.target.value);
                            setRows((list) => list.map((r) => (r.ms_id === row.ms_id ? { ...r, min_stock: v } : r)));
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          fullWidth
                          value={row.max_stock ?? ''}
                          disabled={savingId === row.ms_id}
                          sx={{ minWidth: 0 }}
                          onBlur={(e) => {
                            const v = e.target.value === '' ? null : Number(e.target.value);
                            if (v == null && row.min_stock == null) {
                              saveRow(row, { min_stock: null, max_stock: null });
                              return;
                            }
                            if (v == null || row.min_stock == null) return;
                            saveRow(row, { min_stock: row.min_stock, max_stock: v });
                          }}
                          onChange={(e) => {
                            const v = e.target.value === '' ? null : Number(e.target.value);
                            setRows((list) => list.map((r) => (r.ms_id === row.ms_id ? { ...r, max_stock: v } : r)));
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ overflow: 'hidden' }}>
                        <FormControl fullWidth size="small" error={Boolean(row.processing_plan_archived) && !row.replenish_skip}>
                          <Select
                            displayEmpty
                            value={row.replenish_skip ? '__skip__' : (row.processing_plan_id || '')}
                            disabled={savingId === row.ms_id}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '__skip__') {
                                saveRow(row, { replenish_skip: 1, processing_plan_id: null });
                                return;
                              }
                              saveRow(row, { replenish_skip: 0, processing_plan_id: value || null });
                            }}
                            sx={{ minWidth: 0, maxWidth: '100%' }}
                          >
                            <MenuItem value="">Не выбрана</MenuItem>
                            <MenuItem value="__skip__">Не производить</MenuItem>
                            {(row.techcards || []).map((plan) => (
                              <MenuItem key={plan.id} value={plan.id} disabled={Boolean(plan.archived)}>
                                {plan.name || plan.id}
                                {plan.archived ? ' (архив, недоступна)' : ''}
                                {plan.is_parametric && !plan.archived ? ' (параметрическая)' : ''}
                                {plan.name_process ? ` · ${plan.name_process}` : ''}
                              </MenuItem>
                            ))}
                          </Select>
                          {row.processing_plan_archived && !row.replenish_skip ? (
                            <FormHelperText>Сохранённая техкарта недоступна — выберите живую</FormHelperText>
                          ) : null}
                        </FormControl>
                      </TableCell>
                      {includeStock && (
                        <TableCell align="right">{row.available == null ? '—' : row.available}</TableCell>
                      )}
                      <TableCell>
                        {(() => {
                          const n = forcedBindingsCount(row);
                          return (
                            <Chip
                              size="small"
                              color={n ? 'primary' : 'default'}
                              variant={n ? 'filled' : 'outlined'}
                              label={n ? ruBindingCount(n) : 'Принуд'}
                              onClick={() => setForcedRow(row)}
                            />
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={colSpan} sx={{ py: 0, borderBottom: open ? undefined : 0 }}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 1.5, px: 1, maxWidth: '100%', minWidth: 0 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                              <Typography variant="subtitle2">Связи маркетплейсов</Typography>
                              <Stack direction="row" spacing={1}>
                                <Button size="small" onClick={() => setForcedRow(row)}>задать на складах…</Button>
                                <Button size="small" startIcon={<AddIcon />} onClick={() => openLinkDialog(row)}>Добавить связь</Button>
                              </Stack>
                            </Stack>
                            {(row.links || []).length === 0 ? (
                              <Typography variant="body2" color="text.secondary">Нет привязанных карточек МП</Typography>
                            ) : (
                              <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                                <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ width: 88 }}>МП</TableCell>
                                      <TableCell sx={{ width: '28%' }}>Артикул МП</TableCell>
                                      <TableCell sx={{ width: '22%' }}>Кабинет</TableCell>
                                      <TableCell sx={{ width: '16%' }}>nmID / SKU</TableCell>
                                      <TableCell sx={{ width: '16%' }}>ШК</TableCell>
                                      <TableCell sx={{ width: 72 }}>Вкл</TableCell>
                                      <TableCell sx={{ width: 48 }} />
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {row.links.map((link) => {
                                      const article = ellipsisText(link.article);
                                      const sku = ellipsisText(linkSkuLabel(link));
                                      const match = linkMatchesQuery(link, q);
                                      const barcodes = Array.isArray(link.barcodes) && link.barcodes.length
                                        ? link.barcodes.join(' / ')
                                        : (link.barcode || '');
                                      return (
                                        <TableRow
                                          key={`${link.marketplace}-${link.account_id}-${link.product_key}`}
                                          sx={match ? { bgcolor: 'action.selected' } : undefined}
                                        >
                                          <TableCell>
                                            <Chip size="small" label={MP_LABEL[link.marketplace] || link.marketplace} />
                                          </TableCell>
                                          <EllipsisCell title={link.mp_name ? `${article} · ${link.mp_name}` : article}>
                                            {match ? <strong>{article}</strong> : article}
                                          </EllipsisCell>
                                          <EllipsisCell title={link.account_name || link.account_id}>
                                            {link.account_name || link.account_id || '—'}
                                          </EllipsisCell>
                                          <EllipsisCell title={sku}>{sku}</EllipsisCell>
                                          <EllipsisCell title={barcodes}>{barcodes || '—'}</EllipsisCell>
                                          <TableCell>
                                            <Switch
                                              size="small"
                                              checked={!!link.enabled}
                                              disabled={!productBroadcastOn(row)}
                                              onChange={(e) => toggleLinkEnabled(row, link, e.target.checked)}
                                              inputProps={{ 'aria-label': 'Включить связь' }}
                                              sx={!productBroadcastOn(row) ? { opacity: 0.45 } : undefined}
                                            />
                                          </TableCell>
                                          <TableCell align="right">
                                            <IconButton size="small" onClick={() => removeLink(row, link)} aria-label="Отвязать">
                                              <LinkOffIcon fontSize="small" />
                                            </IconButton>
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={includeStock ? COL_COUNT + 1 : COL_COUNT}>
                    <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                      {q.trim()
                        ? (unlinkedMpArticles.length
                          ? 'Привязки к товару МС нет'
                          : 'Ничего не найдено по товарам МС и артикулам МП (offer_id)')
                        : 'Нет товаров'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Pagination count={pages} page={page} onChange={(_, p) => setPage(p)} />
      </Stack>

      <MsStoresDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={settings}
        setSettings={setSettings}
        stores={stores}
        storeById={storeById}
        runs={runs}
        savingSettings={savingSettings}
        onSaveReplenishment={saveSettings}
        notify={notify}
      />
      <MsProductForcedDialog
        open={!!forcedRow}
        row={forcedRow}
        onClose={() => setForcedRow(null)}
        onSaved={load}
        notify={notify}
      />
      <MsBulkForcedDialog
        open={bulkForcedOpen}
        msIds={[...selectedIds]}
        onClose={() => setBulkForcedOpen(false)}
        onSaved={load}
        notify={notify}
      />

      <Dialog open={!!linkFor} onClose={() => setLinkFor(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Добавить связь МП</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Маркетплейс</InputLabel>
              <Select label="Маркетплейс" value={linkMp} onChange={(e) => changeLinkMp(e.target.value)}>
                <MenuItem value="wb">Wildberries</MenuItem>
                <MenuItem value="ozon">Ozon</MenuItem>
                <MenuItem value="yandex">Yandex</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Кабинет</InputLabel>
              <Select label="Кабинет" value={linkAccount} onChange={(e) => setLinkAccount(e.target.value)}>
                {accounts.map((acc) => (
                  <MenuItem key={acc.id} value={String(acc.id)}>{acc.name || acc.id}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                fullWidth
                label="Поиск товара МП"
                value={mpSearch}
                onChange={(e) => setMpSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') searchMpProducts(); }}
              />
              <Button onClick={searchMpProducts} disabled={!linkAccount}>Найти</Button>
            </Stack>
            <Autocomplete
              options={mpOptions}
              getOptionLabel={(o) => o.vendor_code || o.offer_id || o.name || o.title || String(o.nm_id || o.product_id || '')}
              value={mpPicked}
              onChange={(_, val) => setMpPicked(val)}
              renderInput={(params) => <TextField {...params} label="Товар МП" />}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkFor(null)}>Отмена</Button>
          <Button variant="contained" onClick={addLink} disabled={!mpPicked}>Связать</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(replenishShop)} onClose={() => !replenishing && setReplenishShop(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{replenishShop === 'sewing' ? 'Швейка' : 'Производство'}</DialogTitle>
        <DialogContent>
          {replenishShop === 'sewing' ? (
            <>
              <Typography sx={{ mt: 1 }}>
                Создать в МойСклад задания пошива и кроя по min/max чехлов и одно непроведённое перемещение ткани?
              </Typography>
              <Alert severity="info" sx={{ mt: 2 }}>
                Запускайте после «Производство»: оно уже зарезервирует чехлы под диваны, и швейка добьёт свой min.
                В МойСклад чехлы часто стоят на «Основной техпроцесс» — контур ловит и тип (Швейка / Пошив / Крой),
                и имя карты «Пошив…» / «Крой…». «Нарезка» (поролон) остаётся в производстве. Для теста поставьте min/max на пару чехлов.
              </Alert>
            </>
          ) : (
            <>
              <Typography sx={{ mt: 1 }}>
                Создать в МойСклад производственные задания (диваны, наполнитель, резка, сборка) и одно непроведённое перемещение сырья?
              </Typography>
              <Alert severity="info" sx={{ mt: 2 }}>
                Чехол и крой из техкарты дивана не запускаем в пошив — перемещаем со склада вместе с остальным сырьём.
                Ткань без техкарты уходит в перемещение швейки. После этого прогона запустите «Швейка», чтобы пополнить остатки чехлов.
              </Alert>
            </>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Ручной запуск сразу пишет в МойСклад и не зависит от тумблера cron.
            Создание может занять несколько минут — страница будет ждать в фоне, запрос не оборвётся.
          </Typography>
          {replenishing && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {replenishProgress?.message || 'Создаём в МойСклад…'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplenishShop(null)} disabled={replenishing}>Отмена</Button>
          <Button variant="contained" onClick={runReplenish} disabled={replenishing}>
            {replenishing ? 'Создаём…' : 'Создать в МойСклад'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!matchReport} onClose={() => setMatchReport(null)} maxWidth="lg" fullWidth>
        <DialogTitle>Сопоставление по штрихкоду</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            Сверили все ШК МойСклад со всеми ШК WB, Ozon и Yandex. Один товар МС может получить несколько карточек МП.
            Новые связи создаются выключенными. Связь с другим живым товаром МС не меняется.
            Если карточка висит на архивном или удалённом товаре, а по ШК находится один живой — связь переносится на него и выключается.
            Несколько живых товаров на один ШК — в конфликтах, ничего не пишется.
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <Chip label={`Создано: ${matchReport?.stats?.created ?? 0}`} color="success" size="small" />
            <Chip label={`Перенесено: ${matchReport?.stats?.rebound ?? 0}`} color="info" size="small" />
            <Chip label={`Уже было: ${matchReport?.stats?.already ?? 0}`} size="small" />
            <Chip
              label={`Конфликты: ${matchReport?.stats?.conflicts ?? 0}`}
              color={(matchReport?.stats?.conflicts || 0) ? 'warning' : 'default'}
              size="small"
            />
            <Chip label={`МС со ШК: ${matchReport?.stats?.scanned_ms ?? 0}`} variant="outlined" size="small" />
            <Chip label={`Карточек МП: ${matchReport?.stats?.scanned_mp ?? 0}`} variant="outlined" size="small" />
          </Stack>
          <Tabs value={matchTab} onChange={(_, v) => setMatchTab(v)} sx={{ mb: 1 }}>
            <Tab value="created" label={`Созданные (${(matchReport?.created || []).length})`} />
            <Tab value="rebound" label={`Перенесённые (${(matchReport?.rebound || []).length})`} />
            <Tab value="conflicts" label={`Конфликты (${(matchReport?.conflicts || []).length})`} />
          </Tabs>
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>МП</TableCell>
                  <TableCell>Кабинет</TableCell>
                  <TableCell>Товар МП</TableCell>
                  <TableCell>ШК</TableCell>
                  <TableCell>
                    {matchTab === 'conflicts' ? 'Причина' : matchTab === 'rebound' ? 'Перенос' : 'Товар МС'}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(matchTab === 'conflicts'
                  ? (matchReport?.conflicts || [])
                  : matchTab === 'rebound'
                    ? (matchReport?.rebound || [])
                    : (matchReport?.created || [])).map((row, idx) => (
                  <TableRow key={`${row.marketplace}-${row.account_id}-${row.product_key}-${idx}`}>
                    <TableCell>{MP_LABEL[row.marketplace] || row.marketplace}</TableCell>
                    <TableCell>{row.account_name || row.account_id}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.mp_name || row.product_key}</Typography>
                      <Typography variant="caption" color="text.secondary">{row.product_key}</Typography>
                    </TableCell>
                    <TableCell>{(row.barcodes || []).join(', ') || '—'}</TableCell>
                    <TableCell>
                      {matchTab === 'conflicts' ? (
                        <>
                          <Typography variant="body2">{row.message}</Typography>
                          {row.reason === 'ambiguous_ms' && (
                            <Typography variant="caption" color="text.secondary">
                              {(row.ms_candidates || []).map((c) => msProductLabel(c.ms_name, c.name_ms, c.ms_id)).join(' · ')}
                            </Typography>
                          )}
                          {row.reason === 'mp_already_linked' && (
                            <Typography variant="caption" color="text.secondary">
                              Сейчас: {msProductLabel(row.existing_ms_name, row.ms_name, row.name_ms, row.existing_ms_id, row.ms_id)}. По ШК: {msProductLabel(row.matched_ms_name, row.matched_ms_id)}
                            </Typography>
                          )}
                        </>
                      ) : matchTab === 'rebound' ? (
                        <>
                          <Typography variant="body2">{row.ms_name || row.ms_id}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Было: {msProductLabel(row.previous_ms_name, row.previous_ms_id)}. Трансляция выключена.
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography variant="body2">{row.ms_name || row.ms_id}</Typography>
                          <Typography variant="caption" color="text.secondary">{row.ms_id}</Typography>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {(matchTab === 'conflicts'
                  ? matchReport?.conflicts
                  : matchTab === 'rebound'
                    ? matchReport?.rebound
                    : matchReport?.created)?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography color="text.secondary">
                        {matchTab === 'conflicts'
                          ? 'Конфликтов нет'
                          : matchTab === 'rebound'
                            ? 'Переносов нет'
                            : 'Новых связей нет'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setMatchReport(null)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
